// Content style control for the library page

import React, { useState } from 'react';
import { PaintbrushIcon } from 'lucide-react';
import SidebarPanelItem from '../SidebarPanelItem';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Separator } from '../../../components/ui/separator';
import {
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
} from '../../../components/ui/popover';
import { useSidebar } from '../../../components/ui/sidebar';
import { useLibraryDialog } from '../hooks';
import { useUserSave } from '../../../hooks';
import { analyticsService } from '../../../services/analyticsService';
import { cn } from '@/utilities/cn';
import { contentStyles } from '@airhead-games/shared/configs';
import type {
  ContentStyleConfig,
  UserAuthFields,
} from '@airhead-games/shared/types';

type ContentStyleControlProps = {
  user: UserAuthFields | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const ContentStyleControl: React.FC<ContentStyleControlProps> = ({
  user,
  open,
  onOpenChange,
}) => {
  const { isMobile, state } = useSidebar();
  const collapsedDesktop = !isMobile && state === 'collapsed';
  const { openDialog } = useLibraryDialog();
  const contentStyleId = React.useId();
  const [selectedStyle, setSelectedStyle] = useState<
    ContentStyleConfig['value']
  >(user?.userPreferences.contentStyle ?? 'standard');
  const { markDirty } = useUserSave({
    value: selectedStyle,
    setValue: setSelectedStyle,
    toUpdate: (value) => ({
      userPreferences: {
        contentStyle: value,
      },
    }),
    fromUser: (nextUser) => nextUser.userPreferences.contentStyle,
    errorMessage: 'Unable to save your content style.',
  });

  const content = (
    <>
      <PopoverHeader>
        <PopoverTitle>Content Style</PopoverTitle>
        <PopoverDescription>
          Poppy generates game content to match your preferences. While rare,
          unexpected moments can slip through.
        </PopoverDescription>
      </PopoverHeader>
      <Separator />
      <RadioGroup
        value={selectedStyle}
        onValueChange={(nextValue) => {
          if (!nextValue || nextValue === selectedStyle) {
            return;
          }

          if (!user) {
            openDialog('login');
            return;
          }

          analyticsService.trackPreferenceChanged({
            preference_type: 'content_style',
            preference_value: nextValue,
            link_location: 'library_content_style',
          });
          setSelectedStyle(nextValue as ContentStyleConfig['value']);
          markDirty();
        }}
        className="w-full"
      >
        {contentStyles.map((style) => (
          <div
            key={style.value}
            data-disabled={style.disabled ? 'true' : undefined}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border border-input px-3 py-2',
              'transition-colors hover:bg-muted/50',
              style.value === selectedStyle && 'border-primary bg-muted',
              style.disabled && 'opacity-50'
            )}
          >
            <RadioGroupItem
              id={`${contentStyleId}-${style.value}`}
              value={style.value}
              disabled={style.disabled}
            />
            <Label
              htmlFor={`${contentStyleId}-${style.value}`}
              className={cn(
                'flex-1',
                style.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
            >
              {style.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </>
  );

  return (
    <SidebarPanelItem
      isMobile={isMobile}
      collapsedDesktop={collapsedDesktop}
      open={open}
      onOpenChange={onOpenChange}
      title="Content Style"
      icon={<PaintbrushIcon />}
      label="Content Style"
      isActive={open}
    >
      {content}
    </SidebarPanelItem>
  );
};

export default ContentStyleControl;
